// 付けると外れるカテゴリ（2026-10-29〜、KAWAII の手。クラシック版の excHands＝charat.legacy.js の updateHandVisibility）。
// 設定 exclusive（キー → 外すキー）。そのキーに「なし」（パーツの一覧の先頭）でないパーツを付けたら、外すキーを「なし」にする（両手を付けたら左手・右手を、左手・右手を付けたら両手を）。
// クラシック版は後から付けたほうを戻してしまう組み合わせがあるが、こちらは付けたほうを残す。見え方の決まり（両手と左右の手のどちらを出すか・袖）は lookrules.ts の excHands。
// 状態を書きかえる中身の操作（ops.ts と同じ扱い）。クラシック版の中身（charat.core.min.js）には入れない（ops.ts から呼ぶと組み立てが変わるので、新しい画面の parts.ts・random.ts が呼ぶ）
import { C, catByKey } from './model.js';
import { conf } from '../config.js';
import { partsList } from './data.js';
import { render } from './render.js';
import type { PartState, SaveData } from '../types.js';

export const EXCLUSIVE: Record<string, string[]> = conf('exclusive', {});
// key のパーツが「なし」でなければ、相手を「なし」に。state を渡すと（ランダムの次の状態）そこを書きかえ、draw が偽なら描かない
export function clearExclusive(key: string, state: Record<string, PartState> = C.state, draw = true) {
  const st = state[key], cat = catByKey[key];
  if (!st || !cat || !EXCLUSIVE[key]) return;
  const own = partsList(cat.NAME)[0];
  if (!own || st.parts == null || Number(st.parts) === Number(own.id)) return;
  EXCLUSIVE[key].forEach((k) => {
    const c = catByKey[k], line = state[k], first = c ? partsList(c.NAME)[0] : null;
    if (!c || !line || !first || Number(line.parts) === Number(first.id)) return;
    line.parts = first.id;
    if (draw) render(k, 'part');
  });
}

// 保存データを読む前に、クラシック版と同じ順番で排他を掛け直す（classic/load.ts の replayHandHook と同じ: 手のカテゴリの番号をはじめは空にして、
// 保存データの行の順に先頭の番号を入れながら、行ごとに updateHandVisibility と同じ決まり＝その時点の番号を見て、「なし」でないものを出して相手を隠し「なし」に）。
// クラシック版は番号を「なし」に戻しても絵（読んだときのパーツ）は出したままのことがあるので、ここでは最後に見えていた手を残し（読んだパーツの番号）、隠れた手を「なし」にする。
// 両手・左手・右手が全部入った保存データ（ランダムで作ったものなど）でも、クラシック版で見えていた手と同じになる。新しい画面で作った保存データ（排他がそろっている）は変わらない。
// 読めない行（無いカテゴリ・2 回目の行・無いパーツ）は core/savedata.ts の applySave と同じく飛ばす
export function replayExclusive(save: SaveData): SaveData {
  const keys = Object.keys(EXCLUSIVE).filter((k) => catByKey[k]);
  if (!keys.length || !save || !save.c) return save;
  const first: Record<string, string> = {};
  keys.forEach((k) => { const p = partsList(catByKey[k].NAME)[0]; first[k] = p ? String(p.id) : '1'; });
  const attrs: Record<string, string> = {}, shown: Record<string, boolean> = {};
  keys.forEach((k) => { attrs[k] = ''; shown[k] = true; });
  const seen: Record<string, true> = {}, lineOf: Record<string, number> = {};
  save.c.forEach((line, i) => {
    const cols = line.split(','), key = cols[0], cat = catByKey[key];
    if (!cat || seen[key]) return;
    seen[key] = true;
    const id = cols[1];
    if (id == null || id === '' || !partsList(cat.NAME).some((p) => String(p.id) === String(Number(id)))) return;
    if (key in attrs) { attrs[key] = String(Number(id)); lineOf[key] = i; }
    // updateHandVisibility と同じ: はじめに読んだ番号で、両手 → 左右の手の順に（両手が付いていれば左右を隠して「なし」、左右のどちらかが付いていれば両手を隠して「なし」、左右は付いているほうだけ出す）
    const cap = Object.assign({}, attrs);
    keys.forEach((a) => {
      if (cap[a] === first[a]) return;
      (EXCLUSIVE[a] || []).forEach((b) => {
        if (!(b in attrs)) return;
        attrs[b] = first[b];
        shown[b] = false;
        // 相手の側（両手を外すとき）の、同じ相手を持つほか（左手から見た右手）は、付いているかで出し分ける
        keys.forEach((o) => { if (o !== a && (EXCLUSIVE[b] || []).indexOf(o) >= 0) shown[o] = cap[o] !== first[o]; });
      });
      shown[a] = true;
    });
  });
  const c = save.c.slice();
  Object.keys(lineOf).forEach((k) => {
    const cols = c[lineOf[k]].split(',');
    if (!shown[k] && cols[1] !== first[k]) { cols[1] = first[k]; c[lineOf[k]] = cols.join(','); }
  });
  return Object.assign({}, save, { c: c });
}
